import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, ReportStats, reportMeta } from "./Frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tile, plural, shortDate } from "../kit";
import { cn } from "@/lib/utils";

/**
 * Outstanding requests by clinic — "who is waiting on me?"
 *
 * Built for a phone held one-handed. The answer to the question is a sentence
 * at the top, in words, before any number: Dee should not have to compare five
 * rows to work out which clinic is worst, because the server already did.
 *
 * Everything on this screen is shaped server-side. This component sorts
 * nothing, sums nothing and decides nothing — including which clinic is worst.
 */
export function OutstandingRequests() {
  const data = useQuery(api.reports.outstandingRequests.data);

  return (
    <ReportFrame
      {...reportMeta("outstanding-requests")}
      question="Who is waiting on me?"
      data={data}
      count={data ? data.clinicsWaiting : undefined}
      empty={{
        title: "Nothing outstanding — every request has been actioned",
        body: "No clinic is waiting on an approval, on an order, on a back-order or on a delivery from the warehouse.",
        good: true,
      }}
      footnote={
        data ? (
          <>
            Outstanding counts lines that are requested, approved but not yet
            ordered, or on back-order — the same lines the Requests screen shows
            under Open. Lines picked for the warehouse but not yet sent are
            counted separately.
            {data.orderedFromVendor > 0 ? (
              <>
                {" "}
                A further {data.orderedFromVendor}{" "}
                {data.orderedFromVendor === 1 ? "line is" : "lines are"} already
                ordered from the vendor and are not counted here.
              </>
            ) : null}
          </>
        ) : null
      }
    >
      {data ? (
        <div className="space-y-4">
          {/* The answer, in a sentence, before any table. A callout inside the
              card — border and the registry radius, not a second card. */}
          {data.worstClinicName ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-900">
                {data.worstClinicName} is waiting on you most
              </p>
              <p className="mt-0.5 text-sm text-red-800">{data.worstSummary}</p>
              <p className="num mt-2 text-xs text-red-900">
                {data.totalOpen} {plural(data.totalOpen, "line")} open across{" "}
                {data.clinicsWaiting} {plural(data.clinicsWaiting, "clinic")}
                {data.awaitingDispatch > 0 ? (
                  <> · {data.awaitingDispatch} picked but not sent</>
                ) : null}
                {data.urgentLines > 0 ? (
                  <> · {data.urgentLines} marked urgent</>
                ) : null}
              </p>
            </div>
          ) : null}

          {/* What stage the backlog is stuck at, org-wide. Two columns at
              390px; four across would be unreadable. */}
          <ReportStats>
            <Tile size="sm" label="Need approval" value={data.awaitingApproval} />
            <Tile size="sm" label="Need ordering" value={data.awaitingOrder} />
            <Tile size="sm" label="On back-order" value={data.backOrdered} />
            <Tile
              size="sm"
              label="Picked, not sent"
              value={data.awaitingDispatch}
              hint="not in the count above"
            />
          </ReportStats>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Clinic</TableHead>
                <TableHead className="text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.clinics.map((clinic) => (
                <TableRow key={clinic.clinicId}>
                  <TableCell className="whitespace-normal">
                    <div className="font-medium">{clinic.clinicName}</div>
                    <div className="text-xs text-muted-foreground">
                      {clinic.oldestAgeDays === 0
                        ? "oldest raised today"
                        : `oldest ${clinic.oldestAgeDays}d · ${shortDate(clinic.oldestCreatedAt)}`}
                      {clinic.urgentLines > 0
                        ? ` · ${clinic.urgentLines} urgent`
                        : ""}
                      {clinic.awaitingDispatch > 0
                        ? ` · ${clinic.awaitingDispatch} to send`
                        : ""}
                    </div>
                  </TableCell>
                  {/* The worst clinic is the one somebody has to act on, and
                      red is what that means everywhere else in this app. */}
                  <TableCell
                    className={cn(
                      "num text-right font-medium",
                      clinic.isWorst && "text-red-700",
                    )}
                  >
                    {clinic.openLines}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* The oldest line by name, because "21 days" invites the question
              "twenty-one days of what?" and it should not need a second tap.
              This is the oldest line anywhere, which is not always the worst
              clinic's. */}
          {data.oldestLine ? (
            <p className="text-xs text-muted-foreground">
              Longest wait: {data.oldestLine.itemName} for{" "}
              {data.oldestLine.clinicName},{" "}
              {data.oldestLine.ageDays === 0
                ? "raised today"
                : `${data.oldestLine.ageDays} days since ${shortDate(data.oldestLine.createdAt)}`}
              .
            </p>
          ) : null}

          {data.clearClinics.length > 0 ? (
            <p className="text-xs text-green-800">
              Nothing outstanding at {data.clearClinics.join(", ")}.
            </p>
          ) : null}
        </div>
      ) : null}
    </ReportFrame>
  );
}
